import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageSquarePlus, Users, Plus } from "lucide-react";
import DocumentTitle from "@/components/DocumentTitle";
import { useAuth } from "@/components/AuthProvider";
import { client } from "@/lib/api";
import SignInPrompt from "@/pages/member/SignInPrompt";

/**
 * Wave 15 Step 6 (Vania 3 Oct 2026) · Squad page, now wired.
 *
 * Two tabs:
 *   · yap    → `GET /api/yap/threads` · DMs + squad threads merged, unread badges
 *   · squads → `GET /api/squads/mine` · tap a row to open the Squad detail page
 *
 * Nothing is faked. Empty states remain honest. The "+ new squad" button lives
 * on the squads tab. "Open yap" from inside the Squad detail page lands on
 * `/app/yap/squad:{id}`.
 */
const META = "var(--ww-text-muted)";
const LINE = "var(--ww-line)";
const CARD = "var(--ww-surface)";

export default function SquadPage() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("yap");
  const [threads, setThreads] = useState(null);
  const [squads, setSquads] = useState(null);
  const [newing, setNewing] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    client.get("/yap/threads").then((r) => setThreads(r.data.threads || []))
      .catch(() => setThreads([]));
    client.get("/squads/mine").then((r) => setSquads(r.data.squads || []))
      .catch(() => setSquads([]));
  }, [user]);

  if (!authLoading && !user) {
    return <SignInPrompt title="squad"
                         line="squads are for insiders. get in and start your 30 days free to plan your next one with your people."
                         next="/app/squad" testId="squad-signin" />;
  }

  const createSquad = async () => {
    const name = (newName || "").trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const r = await client.post("/squads", { name });
      setNewing(false); setNewName("");
      const sid = r.data.squad.id;
      setSquads((xs) => [r.data.squad, ...(xs || [])]);
      nav(`/app/squad/${sid}`);
    } catch (e) {
      // Low-friction · silent fail falls back to the empty state hint.
    } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col" data-testid="squad-page"
         style={{ background: "var(--ww-ground)", color: "var(--ww-text)" }}>
      <DocumentTitle title="squad · watu wako" />
      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-10" style={{ overscrollBehavior: "contain" }}>
        <div className="mx-auto" style={{ maxWidth: 760 }}>
          <div className="flex items-center justify-between">
            <h1 className="lowercase" style={{ fontFamily: "Poppins", fontWeight: 800, letterSpacing: "-0.03em", fontSize: 32 }}>
              squad
            </h1>
            {tab === "squads" ? (
              <button data-testid="squad-new-squad" aria-label="new squad"
                      onClick={() => setNewing((v) => !v)}
                      style={{ width: 44, height: 44, borderRadius: 999, background: CARD,
                               border: `1px solid ${LINE}`, color: "var(--ww-text)",
                               display: "grid", placeItems: "center", cursor: "pointer" }}>
                <Plus size={18} />
              </button>
            ) : (
              <Link to="/app/discover" data-testid="squad-new-message" aria-label="start a new thread"
                    style={{ width: 44, height: 44, borderRadius: 999, background: CARD,
                             border: `1px solid ${LINE}`, color: "var(--ww-text)",
                             display: "grid", placeItems: "center", cursor: "pointer" }}>
                <MessageSquarePlus size={18} />
              </Link>
            )}
          </div>

          {newing && (
            <div className="rounded-2xl p-4 mt-4" data-testid="squad-new-squad-form"
                 style={{ background: CARD, border: `1px solid ${LINE}` }}>
              <input value={newName} onChange={(e) => setNewName(e.target.value)}
                     placeholder="name the squad" data-testid="squad-new-squad-name"
                     onKeyDown={(e) => e.key === "Enter" && createSquad()}
                     style={{ width: "100%", padding: 12, borderRadius: 10,
                              background: "var(--ww-sunken)", border: `1px solid ${LINE}`,
                              color: "var(--ww-text)", fontFamily: "Arial", fontSize: 14, outline: "none" }} />
              <div className="flex gap-2 mt-3">
                <button onClick={createSquad} disabled={busy || !newName.trim()}
                        data-testid="squad-new-squad-create"
                        style={{ padding: "10px 20px", borderRadius: 999, background: "#FFD60A",
                                 color: "#1A1A30", border: 0, fontFamily: "Poppins",
                                 fontWeight: 700, textTransform: "lowercase",
                                 cursor: busy ? "wait" : "pointer" }}>
                  create
                </button>
                <button onClick={() => { setNewing(false); setNewName(""); }}
                        style={{ padding: "10px 20px", borderRadius: 999, background: "transparent",
                                 color: META, border: `1px solid ${LINE}`,
                                 fontFamily: "Poppins", fontWeight: 600, textTransform: "lowercase",
                                 cursor: "pointer" }}>
                  cancel
                </button>
              </div>
            </div>
          )}

          <div className="mt-4 flex" data-testid="squad-tabs"
               style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 999, padding: 4, width: "fit-content" }}>
            <TabPill active={tab === "yap"} onClick={() => setTab("yap")} testId="squad-tab-yap">yap</TabPill>
            <TabPill active={tab === "squads"} onClick={() => setTab("squads")} testId="squad-tab-squads">squads</TabPill>
          </div>

          <div className="mt-5" data-testid="squad-list">
            {tab === "yap" ? <YapList threads={threads} /> : <SquadList squads={squads} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function TabPill({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            style={{ padding: "8px 18px", borderRadius: 999,
                     background: active ? "var(--ww-text)" : "transparent",
                     color: active ? "var(--ww-ground)" : "var(--ww-text)",
                     fontFamily: "Poppins", fontWeight: 600, fontSize: 13, textTransform: "lowercase",
                     border: 0, cursor: "pointer", minHeight: 40, minWidth: 60 }}>
      {children}
    </button>
  );
}

function YapList({ threads }) {
  if (threads === null) {
    return <p style={{ fontFamily: "Arial", fontSize: 14, color: META }}>loading…</p>;
  }
  if (threads.length === 0) {
    return <Empty tab="yap" />;
  }
  return (
    <ul data-testid="yap-threads">
      {threads.map((t) => <ThreadRow key={t.id} t={t} />)}
    </ul>
  );
}

function ThreadRow({ t }) {
  const title = t.kind === "squad"
    ? (t.squad?.name || "squad").toLowerCase()
    : "@" + (t.other_user?.handle || "someone").toLowerCase();
  const preview = (() => {
    const m = t.last_message;
    if (!m) return t.kind === "squad" ? "no messages in the squad yet" : "say hi";
    if (m.kind === "text") return m.text;
    if (m.kind === "event_share") return "shared an event";
    if (m.kind === "event_invite") return "sent a private invite";
    return m.text || "·";
  })();
  const avatar = t.kind === "squad"
    ? <SquadAvatar />
    : <PersonAvatar p={t.other_user} />;
  return (
    <li data-testid={`yap-row-${t.id}`}>
      <Link to={`/app/yap/${encodeURIComponent(t.id)}`}
            style={{ display: "flex", alignItems: "center", gap: 12,
                     padding: "14px 4px", borderBottom: `1px solid ${LINE}`,
                     textDecoration: "none", color: "var(--ww-text)" }}>
        {avatar}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15,
                         textTransform: "lowercase" }}>{title}</div>
          <div style={{ fontFamily: "Arial", fontSize: 13, color: META,
                         overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {preview}
          </div>
        </div>
        {t.unread_count > 0 && (
          <span data-testid={`yap-unread-${t.id}`}
                style={{ background: "#FFD60A", color: "#1A1A30", borderRadius: 999,
                         padding: "2px 8px", fontFamily: "Poppins", fontWeight: 700, fontSize: 12 }}>
            {t.unread_count}
          </span>
        )}
      </Link>
    </li>
  );
}

function SquadList({ squads }) {
  if (squads === null) {
    return <p style={{ fontFamily: "Arial", fontSize: 14, color: META }}>loading…</p>;
  }
  if (squads.length === 0) {
    return <Empty tab="squads" />;
  }
  return (
    <ul data-testid="squad-rows">
      {squads.map((s) => (
        <li key={s.id} data-testid={`squad-row-${s.id}`}>
          <Link to={`/app/squad/${s.id}`}
                style={{ display: "flex", alignItems: "center", gap: 12,
                         padding: "14px 4px", borderBottom: `1px solid ${LINE}`,
                         textDecoration: "none", color: "var(--ww-text)" }}>
            <SquadAvatar />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 15,
                             textTransform: "lowercase" }}>{s.name}</div>
              <div style={{ fontFamily: "Arial", fontSize: 13, color: META }}>
                tap to open
              </div>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function Empty({ tab }) {
  const msg = tab === "yap"
    ? "no messages yet. open discover and find your people."
    : "no squads yet. tap + to make one.";
  return (
    <div className="rounded-2xl border p-8 text-center" data-testid={`squad-empty-${tab}`}
         style={{ borderColor: LINE, background: CARD }}>
      <span style={{ width: 56, height: 56, borderRadius: 999, background: "var(--ww-ground)",
                     border: `1px solid ${LINE}`, display: "inline-grid", placeItems: "center", color: META }}>
        <Users size={22} />
      </span>
      <p className="mt-3" style={{ fontFamily: "Arial", fontSize: 14, color: META }}>{msg}</p>
    </div>
  );
}

function PersonAvatar({ p }) {
  const url = p?.picture || p?.photo_url || "";
  if (url) return <img src={url} alt="" style={{ width: 44, height: 44, borderRadius: 999, objectFit: "cover" }} />;
  const letter = (p?.handle || p?.display_name || "?").slice(0, 1).toUpperCase();
  return (
    <div style={{ width: 44, height: 44, borderRadius: 999, background: "var(--ww-sunken)",
                   display: "grid", placeItems: "center", color: "var(--ww-text)",
                   fontFamily: "Poppins", fontWeight: 700 }}>{letter}</div>
  );
}

function SquadAvatar() {
  return (
    <div style={{ width: 44, height: 44, borderRadius: 999, background: "var(--ww-sunken)",
                   border: `1px solid ${LINE}`, display: "grid", placeItems: "center",
                   color: "var(--ww-text)" }}>
      <Users size={20} />
    </div>
  );
}
